import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import classNames from '../../../node_modules/.pnpm/classnames@2.5.1/node_modules/classnames/index.js';
import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as ForwardRef } from '../../assets/icons/CloseIcon24.svg.js';
import { Button } from '../Button/Button.js';
import { Spinner } from '../Spinner/Spinner.js';
import style from './Modal.module.css.js';

const ScaleAnimation = {
    keyframes: [
        {
            transform: "scale(1)",
        },
        {
            transform: "scale(1.03)",
        },
    ],
    options: {
        duration: 75,
        iterations: 1,
    },
};
const Modal = ({ children, footer, className, size = "medium", title, persistent = false, preventCloseOnEscape = false, isLoading = false, open, onClose, returnFocusTo, }) => {
    const modalRef = useRef(null);
    const contentRef = useRef(null);
    const lastFocusedElement = useRef(null);
    const [isRendered, setIsRendered] = useState(open);
    const [modalState, setModalState] = useState(open ? "opening" : "hidden");
    const [isContentOverflowing, setIsContentOverflowing] = useState(false);
    const disableScroll = () => {
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        document.body.style.overflow = "hidden";
        document.body.style.paddingRight = `${scrollbarWidth}px`;
    };
    const enableScroll = () => {
        document.body.style.overflow = "auto";
        document.body.style.paddingRight = "0";
    };
    useEffect(() => {
        if (isRendered) {
            disableScroll();
            return enableScroll;
        }
    }, [isRendered]);
    useEffect(() => {
        if (open) {
            lastFocusedElement.current = document.activeElement;
            setIsRendered(true);
            setModalState("opening");
        }
        else if (isRendered) {
            setModalState("closing");
        }
    }, [isRendered, open]);
    useEffect(() => {
        const checkOverflow = () => {
            if (contentRef.current) {
                const hasOverflow = contentRef.current.scrollHeight > contentRef.current.clientHeight;
                setIsContentOverflowing(hasOverflow);
            }
        };
        if (isRendered && contentRef.current) {
            const resizeObserver = new ResizeObserver(checkOverflow);
            resizeObserver.observe(contentRef.current);
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [isRendered, children]);
    useEffect(() => {
        if (modalState === "visible" &&
            modalRef.current &&
            !modalRef.current.contains(document.activeElement)) {
            modalRef.current.focus();
        }
    }, [modalState]);
    useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.key !== "Escape" && event.key !== "Esc") || !open) {
                return;
            }
            if (modalRef.current && modalRef.current.contains(document.activeElement)) {
                event.stopPropagation();
                if (!preventCloseOnEscape) {
                    setModalState("closing");
                }
                else {
                    modalRef.current?.animate(ScaleAnimation.keyframes, ScaleAnimation.options);
                }
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open, preventCloseOnEscape]);
    return (jsx(Fragment, { children: isRendered &&
            createPortal(jsxs("div", { className: style.wrapper, "data-state": modalState, children: [jsx("div", { className: style.overlay, "data-state": modalState, onClick: () => {
                            if (persistent) {
                                modalRef.current?.animate(ScaleAnimation.keyframes, ScaleAnimation.options);
                            }
                            else {
                                setModalState("closing");
                            }
                        } }), jsxs("div", { className: classNames(className, style.modal, style[size]), "data-state": modalState, onAnimationEnd: () => {
                            if (modalState === "closing") {
                                setIsRendered(false);
                                returnFocusTo ? returnFocusTo.focus() : lastFocusedElement.current?.focus();
                                onClose();
                                setModalState("hidden");
                            }
                            if (modalState === "opening") {
                                setModalState("visible");
                            }
                        }, role: "dialog", "aria-modal": "true", ref: modalRef, tabIndex: -1, children: [jsxs("div", { className: style.header, children: [title && jsx("div", { className: style.title, children: title }), !persistent && (jsx(Button, { className: style.close, onClick: () => {
                                            setModalState("closing");
                                        }, variant: "borderless", children: jsx(ForwardRef, {}) }))] }), isLoading ? (jsx("div", { className: style.loadingContent, children: jsx(Spinner, { size: "sm" }) })) : (jsx("div", { className: style.content, ref: contentRef, children: children })), footer && (jsx("div", { className: classNames(style.footer, isContentOverflowing && style.footerWithBorder), children: footer }))] })] }), document.getElementById("modal-root") ?? document.body) }));
};

export { Modal };
//# sourceMappingURL=Modal.js.map
